/* eslint-disable no-param-reassign */
/**
 * This file contains helpers related to accessing
 * the panorama and search results.
 */
import * as pRetry from 'p-retry';
import { LngLat } from '@tomtom-international/web-sdk-maps/dist/maps.min';

/**
 * Returns bounding rect of specific radius around given coordinates.
 * @param {Object} coordinates - Center point.
 * @param {number} coordinates.lng - Longitude of center point.
 * @param {number} coordinates.lat - Latitude of center point.
 * @param {number} radius - The expected radius of returned area.
 * @returns {Array[number]} - Array containing southwest longitude,
 *     southwest latitude, northeast longitude and northeast latitude of
 *     resulting rect.
 */
export function boundingRect({ lng, lat }, radius) {
    const lngLat = new LngLat(lng, lat);
    const bounds = lngLat.toBounds(radius);

    const { lng: swLng, lat: swLat } = bounds.getSouthWest();
    const { lng: neLng, lat: neLat } = bounds.getNorthEast();

    return [swLng, swLat, neLng, neLat];
}

/**
 * Extracts panorama point information from given XML element
 * representing single point.
 * @param {XMLElement} point - The point XML element containing the
 *     panorama point data.
 * @returns {Object}
 */
function extractPoint({ attributes }) {
    return {
        lng: parseFloat(attributes.longitude.value),
        lat: parseFloat(attributes.latitude.value),
        sessionName: attributes.sessionName.value,
        sequenceNumber: parseInt(attributes.sequenceNumber.value, 10),
        carHeading: parseFloat(attributes.carHeading.value),
        cameraHeading: parseFloat(attributes.cameraHeading.value),
        pitch: parseFloat(attributes.pitch.value),
        roll: parseFloat(attributes.roll.value),
        altitude: parseFloat(attributes.z.value),
    };
}

/**
 * Performs get request against endpoint returned by provided builder to
 * fetch panorama data.
 * @param {Object} options - Request options.
 * @param {Array[number]} options.boundingBox - Bounding box specified
 *     in format returned by boundingRect() function.
 * @param {Function} options.urlBuilder - Function that returns URL to
 *     MOMA API for given endpoint and query arguments.
 * @returns {?Object} - The XML representation describing the nearest
 *     panorama or null in case of an error.
 */
async function fetchPanorama({ boundingBox, urlBuilder }) {
    const bbox = boundingBox.join(',');
    const url = urlBuilder('panorama', {
        bbox,
        info_format: 'text/xml',
        request: 'GetFeatureInfo',
        width: 128,
        height: 128,
        x: 64,
        y: 64,
        version: '1.1.1',
        service: 'WMS',
        srs: 'EPSG:4326',
    });

    const run = async () => {
        const response = await fetch(url);

        if (response.status > 399 && response.status !== 403) {
            throw new pRetry.AbortError(response.statusText);
        }

        return response;
    };

    return pRetry(run, { retries: 5 })
        .then(res => res.text())
        .then((text) => {
            const parser = new DOMParser();
            return parser.parseFromString(text, 'text/xml');
        });
}

/**
 * Fetches panorama data from the MOMA API for given region specified as
 * a rectangular area. The panorama is the nearest one from the center
 * of the rect.
 * @see {@link boundingRect}
 * @param {Object} options - Options for panorama request.
 * @returns {?Object} - The object describing the nearest panorama
 *     or null in case of an error.
 */
export async function panoramaForRect(options) {
    const xml = await fetchPanorama(options);

    const [mainPoint] = xml.getElementsByTagName('MainPoint');
    if (!mainPoint) {
        return null;
    }
    const [point] = mainPoint.getElementsByTagName('point');
    if (!point) {
        return null;
    }

    const [nearestPoints] = xml.getElementsByTagName('NearestPoints');
    if (!nearestPoints) {
        return null;
    }

    const selected = extractPoint(point);
    const nearest = [...xml.getElementsByTagName('point')].map(extractPoint);

    return { selected, nearest };
}

/**
 * Fetches panorama miniature for given rect.
 * @param {Object} options - Options for panorama request.
 * @returns {?String} - The URL for the miniature.
 */
export async function miniatureForRect(options) {
    const xml = await fetchPanorama(options);

    const [mainPoint] = xml.getElementsByTagName('MainPoint');
    if (!mainPoint) {
        return null;
    }

    /*
     * XXX: the panorama data contains `miniature` section, however it
     * returns invalid data. For now we will manually construct the URL.
    const [miniature] = mainPoint.getElementsByTagName('miniature');
    if (!miniature) {
        return null;
    }

    const {
        attributes: { url: { value } } = { url: {} },
    } = miniature;

    return value;
    */

    const [point] = mainPoint.getElementsByTagName('point');
    if (!point) {
        return null;
    }

    const { sessionName, sequenceNumber } = extractPoint(point);
    const { urlBuilder } = options;

    return urlBuilder('momadata/GetFullPanorama', {
        sessionname: sessionName,
        sequencenumber: sequenceNumber,
    });
}

/**
 * Format of displaying address with country code.
 */
export function addressResults(proporties) {
    const { address: { freeformAddress, countryCodeISO3 } = {} } = proporties;
    return `${freeformAddress}, ${countryCodeISO3}`;
}

/**
 * Returns title, based on poi name or location adresses.
 * From highest precedence to lowest: poi name,
 * then streetName, then freeformAddress.
 */
export function titleResult(proporties) {
    const { poi: { name } = {}, address: { streetName, freeformAddress } = {} } = proporties;

    return name || streetName || freeformAddress;
}

const MAX_SHOWN_TRACK_SESSION_NAMES = 15;

async function fetchTrackFeatureInfo({
    boundingBox, urlBuilder, startDate, endDate,
}) {
    const bbox = boundingBox.join(',');
    const url = urlBuilder('track', {
        bbox,
        info_format: 'text/xml',
        request: 'GetFeatureInfo',
        width: 128,
        height: 128,
        x: 64,
        y: 64,
        version: '1.1.1',
        service: 'WMS',
        srs: 'EPSG:4326',
        feature_count: MAX_SHOWN_TRACK_SESSION_NAMES,
        fromdate: startDate,
        todate: endDate,
    });

    const run = async () => {
        const response = await fetch(url);

        if (response.status > 399 && response.status !== 403) {
            throw new pRetry.AbortError(response.statusText);
        }

        return response;
    };

    return pRetry(run, { retries: 5 })
        .then(res => res.text())
        .then((text) => {
            const parser = new DOMParser();
            return parser.parseFromString(text, 'text/xml');
        });
}

async function fetchAreaSessions({
    wkt, urlBuilder, startDate, endDate,
}) {
    const url = urlBuilder('search/FindSessions', {
        fromDate: startDate,
        toDate: endDate,
        wkt,
    });

    const response = await fetch(url);

    if (response.status === 400) {
        const text = await response.text();
        throw new Error(text);
    }

    return response.json();
}


/**
 * Fetches all track session names from the MOMA API for given region specified as
 * a rectangular area.
 * @see {@link boundingRect}
 * @param {Object} options - Options for panorama request.
 * @returns {Array} - The object containing the nearest track session names.
 */
export async function trackSessionNamesForRect(options) {
    try {
        const xml = await fetchTrackFeatureInfo(options);

        const trackElements = xml.querySelectorAll('momatracks');

        if (trackElements.length === 0) {
            return [];
        }

        const filteredTracks = [];

        trackElements.forEach((trackElement) => {
            const sessionNameElement = trackElement.querySelector('sessionname');

            if (sessionNameElement) {
                filteredTracks.push(sessionNameElement.innerHTML.trim());
            }
        });

        return filteredTracks;
    } catch {
        return [];
    }
}


/**
 * Fetches all track session names from the MOMA API for given region specified as
 * a polygon given in WKT format.
 * @param {Object} options - Options for FindSessions request.
 * @returns {Object} - JSON file blob containing the session data.
 */
export async function trackSessionNamesForPolygon(options) {
    try {
        const sessions = await fetchAreaSessions(options);
        return sessions.map(({ SessionName }) => SessionName);
    } catch {
        return [];
    }
}

/**
 * Replace trackSessionNamesForRect function implementation for testing purposes
 * @see {@link trackSessionNamesForRect}
 * @param {Function} newImplementation - New function implementation.
 */
export function stubTrackSessionNamesForRect(newImplementation) {
    // eslint-disable-next-line no-func-assign
    trackSessionNamesForRect = newImplementation;
}

export function setMarkerElementLoadingContent(element) {
    element.innerHTML = `<div class="track-map-marker__container">
        <div class="track-map-marker__progress-dot-container">
            <div class="track-map-marker__progress-dot track-map-marker__progress-dot-0"></div>
            <div class="track-map-marker__progress-dot track-map-marker__progress-dot-1"></div>
            <div class="track-map-marker__progress-dot track-map-marker__progress-dot-2"></div>
        </div>
    </div>`;
}

export function setMarkerElementSessionNamesContent({
    element, sessionNames, onCloseClick, onCopyClick, isPolygon = false,
}) {
    if (sessionNames.length === 0) {
        element.innerHTML = '';

        return;
    }

    const title = isPolygon ? 'Sessions in the drawn area' : 'Sessions near this point';

    element.innerHTML = `<div class="track-map-marker__container">
        <div class="track-map-marker__row">
            <span class="track-map-marker__heading">${title}:</span>
            <div class="track-map-marker__heading-close-button">&times;</div>
        </div>
        <ul class="track-map-marker__list"></ul>
        <div class="button is-secondary is-medium track-map-marker__copy-button">
            COPY TO CLIPBOARD
        </div>
    </div>`;

    sessionNames.forEach((sessionName) => {
        const listElement = document.createElement('li');
        listElement.innerHTML = sessionName;

        element.querySelector('ul').append(listElement);
    });

    element.querySelector('.track-map-marker__heading-close-button').addEventListener(
        'click',
        onCloseClick,
    );

    element.querySelector('.track-map-marker__copy-button').addEventListener(
        'click',
        onCopyClick,
    );
}
